/*
 * Copyright (c) 2020, WSO2 Inc. (http://www.wso2.com). All Rights Reserved.
 *
 * This software is the property of WSO2 Inc. and its suppliers, if any.
 * Dissemination of any information or reproduction of any material contained
 * herein is strictly forbidden, unless permitted by WSO2 in accordance with
 * the WSO2 Commercial License available at http://wso2.com/licenses.
 * For specific language governing the permissions and limitations under
 * this license, please see the license as well as any agreement you’ve
 * entered into with WSO2 governing the purchase of this software and any
 * associated services.
 */

import React, {useState} from 'react';

import TextField, { TextFieldProps } from '@material-ui/core/TextField';
import { makeStyles} from '@material-ui/core/styles';
import { IconButton, InputAdornment, Zoom } from '@material-ui/core';
import Visibility from '@material-ui/icons/VisibilityOutlined';
import VisibilityOff from '@material-ui/icons/VisibilityOffOutlined';

import CustomIcons from '../icons/CustomIcons';
import DarkTooltip from './DarkTooltip';

const useStyles = makeStyles((theme) => ({
    accessTokenField: {      
        '& .MuiFilledInput-root': {
            borderRadius: `${theme.spacing(0.5)}px`,
            backgroundColor: theme.palette.common.white,
            paddingLeft: 10,
            fontSize: 12,
            color: '#8D91A3',
            letterSpacing: 1,
        },
        '& .MuiFilledInput-input': {
            paddingTop: theme.spacing(1.5),
            paddingBottom: theme.spacing(1.5),    
        },
        '& .MuiFilledInput-underline:after': {
            display: "none"
        },
    },
    outline: {
        "&&&:before": {
            borderBottom: "none"
        },
        "&&:after": {
            borderBottom: "none"
        }
    },
    iconBtn: {
        color: theme.palette.grey[500]
    },
    copyIcon: {
        cursor: "pointer"
    },
    copy: {
        padding: 0,
        '&:hover': {
            backgroundColor: "transparent"
        }
    },
    copyIconButton: {
        color: theme.palette.grey[500],
        height: `${theme.spacing(5.625)}px`,
        width: `${theme.spacing(5.625)}px`,
        padding: 0
    },
}));

export interface PasswordCopyFieldProps {
    value: string,
    onChange?: ((event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => void) | undefined,
    disabled?: boolean,
    name?: string,
    className?: string,
    id?: string,
    showCopyBtn?: boolean
}

export default function PasswordCopyField(props: PasswordCopyFieldProps) {

    const classes = useStyles();
    const [copiedTooltipVisible, setCopiedTooltipVisible] = useState(false);
    const { value, onChange, disabled, name, className, id, showCopyBtn} = props;
    const [showToken, setShowToken] = useState(false);
    const copyBtnVisibility = showCopyBtn === undefined ? true : showCopyBtn;

    function handleClickShowToken() {
        setShowToken(!showToken);
    }

    const handleCopyClick = () => {
        navigator.clipboard.writeText(value as string);
        setCopiedTooltipVisible(true);
        setTimeout(() => {
            setCopiedTooltipVisible(false);
        }, 2000);
    }

    return (
        <TextField
            variant='filled'
            name={name}
            value={value}
            fullWidth={true}
            disabled={disabled}
            InputLabelProps={{ shrink: false }}
            classes={{root: classes.accessTokenField}}
            className={className}
            onChange={onChange}
            type={showToken ? 'text' : 'password'}
            id={id} 
            InputProps={{
                classes: { 
                    underline: classes.outline,
                },
                style: {
                    paddingBottom: 0,
                },
                endAdornment: (
                    <InputAdornment position='end'>
                        <IconButton
                            edge='end'
                            aria-label='Toggle token visibility'
                            onClick={handleClickShowToken}
                            className={classes.iconBtn}
                        >
                            {showToken ? <VisibilityOff />
                                : <Visibility />}
                        </IconButton>
                        { copyBtnVisibility ?
                            <DarkTooltip title={'Copied'} TransitionComponent={Zoom} placement="right" open={copiedTooltipVisible} >
                                <IconButton onClick={handleCopyClick} className={classes.copyIconButton} edge='end'>
                                    <CustomIcons icon={'copy-icon'} />
                                </IconButton>
                            </DarkTooltip>
                            : null
                        }
                        
                    </InputAdornment>    
                )
            }}
            data-testid={id}
        />
    )
}
